import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@folio/components';
import { IntermediaryUsersListModule } from '../IntermediaryUsersListModule/IntermediaryUsersListModule';
import { TeamManagementModule } from '../TeamManagementModule/TeamManagementModule';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  brokerControllerListBrokersQueryOptions,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerListChildBrokerageFirmsQueryOptions,
} from 'korint-sdk';
import { useKorintModuleConfig } from 'korint-sdk/react';
import { Stack } from '@folio/components/ui/flex';

function UserManagementRecapCard() {
  const { t } = useTranslation();
  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { showTeamManagement } = useKorintModuleConfig('intermediaryUserManagement');

  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(user?.brokerageFirmId!),
    enabled: !!user?.brokerageFirmId,
  });

  const showTeams = brokerageFirm?.firmType
    ? (showTeamManagement?.includes(brokerageFirm.firmType) ?? false)
    : false;

  const {
    data: brokers,
    isPending: brokersPending,
    isError: brokersError,
  } = useQuery({
    ...brokerControllerListBrokersQueryOptions(user?.brokerageFirmId!, {
      count: 1,
      page: 1,
    }),
    enabled: !!user?.brokerageFirmId,
    select: data => data.totalCount,
  });

  const {
    data: activeTeams,
    isPending: activeTeamsPending,
    isError: activeTeamsError,
  } = useQuery({
    ...viewSpecificControllerListChildBrokerageFirmsQueryOptions(user?.brokerageFirmId!, {
      page: 1,
      count: 1,
      hasSomeDistributionRequestWithStatus: ['APPROVED'],
    }),
    enabled: !!user?.brokerageFirmId && showTeams,
    select: data => data.totalCount,
  });

  const isLoading = brokersPending || (showTeams && activeTeamsPending);
  const hasError = brokersError || (showTeams && activeTeamsError);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('intermediaryUserManagement.overviewTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4 animate-pulse">
            <div className="rounded-md p-4 bg-gray-10 h-[80px]" />
            {showTeams && <div className="rounded-md p-4 bg-gray-10 h-[80px]" />}
          </div>
        ) : hasError ? (
          <div className="text-center text-muted-foreground">
            {t('intermediaryUserManagement.errorMessage')}
          </div>
        ) : (
          <div className={`grid grid-cols-1 gap-6 mt-4 ${showTeams ? 'md:grid-cols-2' : ''}`}>
            <div className="rounded-md p-4 bg-gray-10">
              <div className="text-sm text-gray-500">
                {t('intermediaryUserManagement.activeUsers')}
              </div>
              <div className="text-2xl font-semibold">{brokers}</div>
            </div>
            {showTeams && (
              <div className="rounded-md p-4 bg-gray-10">
                <div className="text-sm text-gray-500">
                  {t('intermediaryUserManagement.activeTeams')}
                </div>
                <div className="text-2xl font-semibold">{activeTeams}</div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function IntermediaryUserManagementModule() {
  const { t } = useTranslation();
  const { showTeamManagement } = useKorintModuleConfig('intermediaryUserManagement');

  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(user?.brokerageFirmId!),
    enabled: !!user?.brokerageFirmId,
  });

  const showTeams = brokerageFirm?.firmType
    ? showTeamManagement?.includes(brokerageFirm.firmType)
    : false;

  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') === 'teams' ? 'teams' : 'users';

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  if (!showTeams) {
    return (
      <>
        <UserManagementRecapCard />
        <IntermediaryUsersListModule />
      </>
    );
  }

  return (
    <>
      <UserManagementRecapCard />
      <Tabs value={currentTab} onValueChange={handleTabChange}>
        <TabsList className={showTeams ? 'grid grid-cols-2' : ''}>
          <TabsTrigger value="users">{t('intermediaryUserManagement.usersTabTitle')}</TabsTrigger>
          {showTeams && (
            <TabsTrigger value="teams">{t('intermediaryUserManagement.teamsTabTitle')}</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="users">
          <Stack>
            <IntermediaryUsersListModule />
          </Stack>
        </TabsContent>
        <TabsContent value="teams">
          <TeamManagementModule />
        </TabsContent>
      </Tabs>
    </>
  );
}
